import { useFieldArray, useFormContext } from "react-hook-form";
import { AiOutlinePlus } from "react-icons/ai";
import { BsTrash } from "react-icons/bs";
import Label from "../../../../../../components/form_fields/Label";
import {
	useGetPaymentContactTypes,
	useGetPaymentRegions,
} from "../../../../../../services/miscellaneous-payment.service";
import { FieldError, PhoneField, SectionHeading } from "../components";
import {
	EMAIL_PATTERN,
	PaymentSetupFormValues,
	emptyContact,
	inputClassName,
	toList,
} from "../helpers";

type Region = {
	regionId: string;
	region: string;
	districts?: { districtId: string; districtName: string }[];
};

const ContactStep = () => {
	const {
		register,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<PaymentSetupFormValues>();

	const { fields, append, remove } = useFieldArray({ control, name: "contacts" });

	const { data: regionsResponse } = useGetPaymentRegions();
	const { data: contactTypesResponse } = useGetPaymentContactTypes();
	const regions = toList<Region>(regionsResponse);
	const contactTypes = toList<{ id: string; name: string }>(contactTypesResponse);

	const selectedRegionId = watch("officeAddress.officeRegion");
	const districts =
		regions.find((region) => region.regionId === selectedRegionId)?.districts || [];
	const hasStreetAddress = !!watch("officeAddress.officeAddress")?.trim();

	return (
		<div className="space-y-10">
			<div>
				<SectionHeading
					title="Primary contact"
					description="The person the provider should contact about this request."
				/>
				<div className="grid grid-cols-2 gap-x-6 gap-y-5">
					<div>
						<Label for="surname" title="Surname" />
						<input
							id="surname"
							placeholder="Enter surname"
							{...register("surname", { required: "Enter the contact's surname" })}
							className={inputClassName(!!errors.surname)}
						/>
						<FieldError error={errors.surname} />
					</div>
					<div>
						<Label for="otherNames" title="Other names" />
						<input
							id="otherNames"
							placeholder="Enter other names"
							{...register("otherNames", { required: "Enter the contact's other names" })}
							className={inputClassName(!!errors.otherNames)}
						/>
						<FieldError error={errors.otherNames} />
					</div>
					<div>
						<Label for="email" title="Email" />
						<input
							id="email"
							type="email"
							placeholder="name@company.com"
							{...register("email", {
								required: "Enter the contact's email",
								pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" },
							})}
							className={inputClassName(!!errors.email)}
						/>
						<FieldError error={errors.email} />
					</div>
					<div>
						<Label for="phoneNumber" title="Phone number" />
						<PhoneField
							name="phoneNumber"
							control={control}
							required="Enter the contact's phone number"
							error={errors.phoneNumber}
						/>
					</div>
				</div>
			</div>

			<div>
				<SectionHeading title="Office address" description="Optional, but speeds up review." />
				<div className="grid grid-cols-2 gap-x-6 gap-y-5">
					<div className="col-span-2">
						<Label for="officeAddress" title="Street address" optional />
						<input
							id="officeAddress"
							placeholder="e.g. 12 Ring Road East, Accra"
							{...register("officeAddress.officeAddress", {
								maxLength: { value: 100, message: "Use 100 characters or fewer" },
							})}
							className={inputClassName(!!errors.officeAddress?.officeAddress)}
						/>
						<FieldError error={errors.officeAddress?.officeAddress} />
					</div>
					<div>
						<Label for="officeRegion" title="Region" optional />
						<select
							id="officeRegion"
							{...register("officeAddress.officeRegion", {
								onChange: () => setValue("officeAddress.officeDistrict", ""),
							})}
							disabled={!hasStreetAddress}
							className={inputClassName()}
						>
							<option value="">Select region</option>
							{regions.map((region) => (
								<option key={region.regionId} value={region.regionId}>
									{region.region}
								</option>
							))}
						</select>
					</div>
					<div>
						<Label for="officeDistrict" title="District" optional />
						<select
							id="officeDistrict"
							{...register("officeAddress.officeDistrict")}
							disabled={!hasStreetAddress || !districts.length}
							className={inputClassName()}
						>
							<option value="">Select district</option>
							{districts.map((district) => (
								<option key={district.districtId} value={district.districtId}>
									{district.districtName}
								</option>
							))}
						</select>
					</div>
					<div>
						<Label for="officeCity" title="City / town" optional />
						<input
							id="officeCity"
							placeholder="e.g. Accra"
							disabled={!hasStreetAddress}
							{...register("officeAddress.officeCity", {
								maxLength: { value: 50, message: "Use 50 characters or fewer" },
							})}
							className={inputClassName(!!errors.officeAddress?.officeCity)}
						/>
						<FieldError error={errors.officeAddress?.officeCity} />
					</div>
					<div>
						<Label for="officeGpsAddress" title="GhanaPost GPS address" optional />
						<input
							id="officeGpsAddress"
							placeholder="e.g. GA-183-4290"
							disabled={!hasStreetAddress}
							{...register("officeAddress.officeGpsAddress", {
								maxLength: { value: 50, message: "Use 50 characters or fewer" },
							})}
							className={inputClassName(!!errors.officeAddress?.officeGpsAddress)}
						/>
						<FieldError error={errors.officeAddress?.officeGpsAddress} />
					</div>
					<div>
						<Label for="officeOwnership" title="Premises" optional />
						<select
							id="officeOwnership"
							disabled={!hasStreetAddress}
							{...register("officeAddress.officeOwnership")}
							className={inputClassName()}
						>
							<option value="">Select</option>
							<option value="owned">Owned</option>
							<option value="rented">Rented</option>
						</select>
					</div>
					<div />
					<div>
						<Label for="officePhone" title="Office landline" optional />
						<PhoneField
							name="officeAddress.officePhone"
							control={control}
							ghanaOnly
							error={errors.officeAddress?.officePhone}
						/>
					</div>
					<div>
						<Label for="officeMobile" title="Office mobile" optional />
						<PhoneField
							name="officeAddress.officeMobile"
							control={control}
							ghanaOnly
							error={errors.officeAddress?.officeMobile}
						/>
					</div>
				</div>
			</div>

			<div>
				<SectionHeading
					title="Departmental contacts"
					description="Optional. For example, who to contact in Finance or Technical."
				/>
				<div className="space-y-4">
					{fields.map((field, index) => {
						const contactErrors = errors.contacts?.[index];
						return (
							<div
								key={field.id}
								className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4 items-start rounded-lg border border-gray-200 p-4"
							>
								<div>
									<Label for={`contacts.${index}.name`} title="Department" />
									<select
										id={`contacts.${index}.name`}
										{...register(`contacts.${index}.name`, {
											required: "Select a department",
										})}
										className={inputClassName(!!contactErrors?.name)}
									>
										<option value="">Select</option>
										{contactTypes.map((type) => (
											<option key={type.id || type.name} value={type.name}>
												{type.name}
											</option>
										))}
									</select>
									<FieldError error={contactErrors?.name} />
								</div>
								<div>
									<Label for={`contacts.${index}.email`} title="Email" />
									<input
										id={`contacts.${index}.email`}
										type="email"
										placeholder="finance@company.com"
										{...register(`contacts.${index}.email`, {
											required: "Enter an email",
											pattern: {
												value: EMAIL_PATTERN,
												message: "Enter a valid email address",
											},
										})}
										className={inputClassName(!!contactErrors?.email)}
									/>
									<FieldError error={contactErrors?.email} />
								</div>
								<div>
									<Label for={`contacts.${index}.phone`} title="Phone" />
									<PhoneField
										name={`contacts.${index}.phone`}
										control={control}
										required="Enter a phone number"
										error={contactErrors?.phone}
									/>
								</div>
								<button
									type="button"
									onClick={() => remove(index)}
									title="Remove contact"
									className="mt-11 text-gray-400 hover:text-red-500"
								>
									<BsTrash />
								</button>
							</div>
						);
					})}
					<button
						type="button"
						onClick={() => append({ ...emptyContact })}
						className="flex items-center gap-1 text-sm text-primary-blue"
					>
						<AiOutlinePlus /> Add contact
					</button>
				</div>
			</div>
		</div>
	);
};

export default ContactStep;
